import { Injectable, OnDestroy } from '@angular/core';
import { filter, fromEvent, Subscription } from 'rxjs';

type Modifier = 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey';
type PlatformModifier = Modifier | 'primaryModifier';

export interface ShortcutConfig {
  key: string;
  modifiers?: PlatformModifier[];
  preventDefault?: boolean;
  callback: (event: KeyboardEvent) => void;
  force?: boolean;
}

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService implements OnDestroy {
  private subscriptions = new Map<string, Subscription>();
  private readonly isMac = navigator.platform.toUpperCase().includes('MAC');

  registerShortcut(config: ShortcutConfig): boolean {
    const resolvedModifiers = this.resolveModifiers(config.modifiers || []);
    const resolvedConfig = { ...config, modifiers: resolvedModifiers };

    const id = this.getShortcutId(resolvedConfig);

    if (this.subscriptions.has(id)) {
      if (!config.force) {
        return false;
      }

      this.subscriptions.get(id)?.unsubscribe();
      this.subscriptions.delete(id);
    }

    const sub = fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(filter((event) => this.matchesShortcut(event, resolvedConfig)))
      .subscribe((event) => {
        if (resolvedConfig.preventDefault) {
          event.preventDefault();
        }
        resolvedConfig.callback(event);
      });

    this.subscriptions.set(id, sub);
    return true;
  }

  unregisterShortcut(config: ShortcutConfig): boolean {
    const resolvedModifiers = this.resolveModifiers(config.modifiers || []);
    const resolvedConfig = { ...config, modifiers: resolvedModifiers };

    const id = this.getShortcutId(resolvedConfig);

    const sub = this.subscriptions.get(id);
    if (sub) {
      sub.unsubscribe();
      this.subscriptions.delete(id);
      return true;
    }

    return false;
  }

  private resolveModifiers(mods: PlatformModifier[]): Modifier[] {
    return mods.map((mod) =>
      mod === 'primaryModifier' ? (this.isMac ? 'metaKey' : 'ctrlKey') : mod
    );
  }

  private matchesShortcut(
    event: KeyboardEvent,
    config: ShortcutConfig
  ): boolean {
    if (!this.matchesKey(event, config.key)) {
      return false;
    }

    const modifiers = config.modifiers || [];
    const allModifiersMatch = modifiers.every(
      (mod) => event[mod as keyof KeyboardEvent]
    );
    const noUnexpectedModifiers =
      (!event.metaKey || modifiers.includes('metaKey')) &&
      (!event.ctrlKey || modifiers.includes('ctrlKey')) &&
      (!event.altKey || modifiers.includes('altKey')) &&
      (!event.shiftKey || modifiers.includes('shiftKey'));

    return allModifiersMatch && noUnexpectedModifiers;
  }

  /**
   * ⌘E / Ctrl+E did nothing on most machines but the one it was written on:
   * `event.key` is the character the *active layout* produces, and with the
   * Ukrainian or Russian layout on the physical E key is "у" — the shortcut
   * only answered while the English layout was on. Legacy matched the
   * physical key (`e.code == 'KeyE'`), and so does this now; `key` stays as a
   * second way in, for layouts that put the letter elsewhere (Dvorak) and
   * for keys that are not letters or digits.
   */
  private matchesKey(event: KeyboardEvent, key: string): boolean {
    const wanted = key.toLowerCase();
    const physical = /^[a-z]$/.test(wanted)
      ? `Key${wanted.toUpperCase()}`
      : /^[0-9]$/.test(wanted)
        ? `Digit${wanted}`
        : null;

    return (
      (physical !== null && event.code === physical) ||
      event.key?.toLowerCase() === wanted
    );
  }

  private getShortcutId(config: ShortcutConfig): string {
    const mods = (config.modifiers || []).sort().join('+');
    return mods
      ? `${mods}+${config.key.toLowerCase()}`
      : config.key.toLowerCase();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions.clear();
  }
}
